import React, { useEffect, useState } from 'react';
import { 
    FaSearch, FaTrash, FaEdit, FaFilePdf, FaCheck, FaUndoAlt, FaHistory 
} from 'react-icons/fa';
import { 
    fetchFactures, fetchAvoirs, createAvoir, updateFacture, deleteFacture, exportFacturePDF 
} from '../api'; 

const FactureAvoirPage = ({ user }) => {
    const [avoirs, setAvoirs] = useState([]);
    const [facturesDefinitives, setFacturesDefinitives] = useState([]);
    
    // --- ÉTATS FORMULAIRE ---
    const [editingId, setEditingId] = useState(null);
    const [invoiceSearch, setInvoiceSearch] = useState('');
    const [showDropdown, setShowDropdown] = useState(false); 
    
    const [selectedParentNumber, setSelectedParentNumber] = useState('');
    const [clientName, setClientName] = useState('');
    const [dateEmission, setDateEmission] = useState(new Date().toISOString().split('T')[0]);
    const [currentLines, setCurrentLines] = useState([]);
    const [sommeRecue, setSommeRecue] = useState(0);

    const [message, setMessage] = useState(null);
    const [searchHistory, setSearchHistory] = useState("");
    
    const [downloadingPdfId, setDownloadingPdfId] = useState(null);

    const currentYearStr = new Date().getFullYear().toString().slice(-2);
    const defaultPrefix = `FD/ARSN.${currentYearStr}/`;

    const totalPrestationOriginale = currentLines.reduce((acc, l) => acc + (Number(l.total) || 0), 0);
    const netADeduire = Number(sommeRecue) > totalPrestationOriginale ? Number(sommeRecue) - totalPrestationOriginale : 0;

    const loadData = async () => {
        try {
            const [resDef, resAv] = await Promise.all([fetchFactures(), fetchAvoirs()]);
            const defs = (resDef.data || []).filter(f => f.type === 'final' || f.type === 'facture' || !f.type);
            setFacturesDefinitives(defs);
            setAvoirs(resAv.data || []);
            if (!editingId) setInvoiceSearch(defaultPrefix);
        } catch (err) { setMessage({ type: 'error', text: 'Erreur de chargement.' }); }
    };

    useEffect(() => { loadData(); }, []);

    const handleSelectFacture = (fact) => {
        setInvoiceSearch(fact.number);
        setSelectedParentNumber(fact.number);
        setClientName(fact.clientName);
        
        const lines = (fact.lines || []).map(l => ({
            code: l.code,
            name: l.name,
            details: l.details,
            price: l.price,
            quantity: l.quantity,
            total: l.total 
        }));
        
        setCurrentLines(lines);
        setSommeRecue(fact.amountPaid || 0);
        setShowDropdown(false); 
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Supprimer cet avoir ?")) return;
        try { await deleteFacture(id); loadData(); } catch (e) { console.error(e); }
    };

    const startEdit = (a) => {
        setEditingId(a._id);
        setSelectedParentNumber(a.parentInvoice);
        setClientName(a.clientName);
        setInvoiceSearch(a.parentInvoice);
        setDateEmission(new Date(a.date).toISOString().split('T')[0]);
        
        const original = facturesDefinitives.find(f => f.number === a.parentInvoice);
        if (original) setCurrentLines(original.lines);

        const avrLine = a.lines.find(l => l.code === 'AVR');
        if (avrLine) {
            setSommeRecue(avrLine.price + (original?.totalHT || 0));
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedParentNumber || netADeduire <= 0) return;

        const finalLines = [
            ...currentLines.map(l => ({ ...l, price: 0, total: 0 })), 
            { 
                code: 'AVR', 
                name: `Avoir sur trop-perçu (Facture ${selectedParentNumber})`, 
                details: `Reçu: ${sommeRecue} F - Prestation: ${totalPrestationOriginale} F`, 
                price: Number(netADeduire), 
                quantity: 1, 
                total: Number(netADeduire) 
            }
        ];

        const payload = { 
            clientName, lines: finalLines, date: dateEmission, 
            parentInvoice: selectedParentNumber, type: 'avoir' 
        };

        try {
            if (editingId) await updateFacture(editingId, payload);
            else await createAvoir(payload);
            
            setMessage({ type: 'success', text: 'Enregistré avec succès.' });
            setEditingId(null); setSelectedParentNumber(''); setClientName(''); 
            setCurrentLines([]); setSommeRecue(0); setInvoiceSearch(defaultPrefix);
            loadData();
            setTimeout(() => setMessage(null), 2000);
        } catch (err) { setMessage({ type: 'error', text: 'Erreur lors de l\'enregistrement.' }); }
    };

    const handleDownloadPDF = async (id, number) => {
        setDownloadingPdfId(id);
        try {
            const res = await exportFacturePDF(id);
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${number.replace(/\//g, '-')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setMessage({ type: 'error', text: 'Erreur lors du téléchargement du PDF.' });
            setTimeout(() => setMessage(null), 3000);
        } finally {
            setDownloadingPdfId(null);
        }
    };

    return (
        <div className="container-fluid py-4 px-3 px-md-4">
            
            {/* HEADER */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-2">
                <div>
                    <h4 className="fw-bold mb-0 text-uppercase text-dark" style={{ letterSpacing: '1px' }}>
                        <FaUndoAlt className="me-2 text-danger"/> RÉGULARISATION / AVOIR
                    </h4>
                    <p className="small mb-0 mt-1 text-muted" style={{ letterSpacing: '1px' }}>Création de facture d'avoir sur trop-perçu</p>
                </div>
            </div>

            {/* MESSAGES D'ALERTE */}
            {message && (
                <div className={`alert py-2 shadow-sm border-0 ${message.type === 'error' ? 'alert-danger' : 'alert-success'}`}>
                    {message.text}
                </div>
            )}

            {/* FORMULAIRE D'ÉMISSION D'AVOIR */}
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-header bg-white py-3 fw-bold text-uppercase text-danger" style={{ letterSpacing: '1px' }}>
                    {editingId ? "MODIFIER L'AVOIR" : "NOUVEL AVOIR"}
                </div>
                <div className="card-body p-4">
                    <form onSubmit={handleSubmit}>
                        <div className="row g-3 mb-4">
                            <div className="col-12 col-md-4 position-relative">
                                <label className="small fw-bold mb-1 text-muted">N° FACTURE FD</label>
                                <input type="text" className="form-control form-control-sm" 
                                    value={invoiceSearch} 
                                    onChange={(e) => {setInvoiceSearch(e.target.value); setShowDropdown(true);}} 
                                    onFocus={() => setShowDropdown(true)}
                                    placeholder="Ex: FD/ARSN.26/001"
                                />
                                {showDropdown && (
                                    <div className="position-absolute w-100 shadow bg-white border rounded mt-1" style={{zIndex: 999, maxHeight: '150px', overflowY: 'auto'}}>
                                        {facturesDefinitives.filter(f => f.number.includes(invoiceSearch)).map(f => (
                                            <div key={f._id} className="p-2 border-bottom cursor-pointer text-dark bg-light-hover" 
                                                onMouseDown={() => handleSelectFacture(f)} style={{cursor: 'pointer'}}>
                                                <span className="fw-bold">{f.number}</span> - <span className="text-muted small">{f.clientName}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="col-12 col-md-5">
                                <label className="small fw-bold mb-1 text-muted">CLIENT</label>
                                <input type="text" className="form-control form-control-sm bg-light" value={clientName} readOnly placeholder="Sélectionnez une facture..." />
                            </div>
                            <div className="col-12 col-md-3">
                                <label className="small fw-bold mb-1 text-muted">DATE D'ÉMISSION</label>
                                <input type="date" className="form-control form-control-sm" value={dateEmission} onChange={(e) => setDateEmission(e.target.value)} />
                            </div>
                        </div>

                        {/* ZONE DE CALCUL */}
                        <div className="row g-3 mb-4 align-items-center p-3 bg-light border rounded">
                            <div className="col-12 col-md-4">
                                <label className="small fw-bold mb-1 text-danger">SOMME REÇUE DU CLIENT (FCFA)</label>
                                <input type="number" className="form-control fw-bold text-end text-danger" 
                                    value={sommeRecue === 0 ? '' : sommeRecue} 
                                    onChange={(e) => setSommeRecue(e.target.value)} 
                                    placeholder="0"
                                />
                            </div>
                            <div className="col-12 col-md-4 text-center my-2 my-md-0">
                                <span className="text-muted small">Prix Prestation Originale :</span> <br /> 
                                <span className="fw-bold fs-6 text-dark">{totalPrestationOriginale.toLocaleString()} F</span>
                            </div>
                            <div className="col-12 col-md-4 text-center text-md-end">
                                <div className={`badge px-3 py-2 fs-6 ${netADeduire > 0 ? 'bg-danger text-white' : 'bg-secondary text-white'}`}>
                                    NET À DÉDUIRE : {netADeduire.toLocaleString()} F
                                </div>
                            </div>
                        </div>

                        {/* LIGNES DE LA FACTURE ORIGINALE */}
                        <div className="table-responsive mb-4 border rounded">
                            <table className="table table-sm mb-0 text-nowrap table-hover align-middle">
                                <thead className="bg-light">
                                    <tr className="text-uppercase text-muted small">
                                        <th className="ps-3 py-2" style={{minWidth:'80px'}}>Code</th>
                                        <th className="py-2" style={{minWidth:'200px'}}>Désignation</th>
                                        <th className="py-2" style={{minWidth:'150px'}}>Détail</th>
                                        <th className="text-center py-2" style={{minWidth:'100px'}}>PU (HT)</th>
                                        <th className="text-center py-2" style={{minWidth:'60px'}}>Qté</th>
                                        <th className="text-end pe-3 py-2" style={{minWidth:'120px'}}>Montant (HT)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentLines.length === 0 ? (
                                        <tr><td colSpan="6" className="text-center py-4 text-muted"><em>Veuillez sélectionner une facture définitive.</em></td></tr>
                                    ) : (
                                        currentLines.map((l, i) => (
                                            <tr key={i}>
                                                <td className="ps-3 text-muted fw-bold small">{l.code}</td>
                                                <td className="fw-bold text-wrap text-dark" style={{minWidth:'200px'}}>{l.name}</td>
                                                <td className="text-muted text-wrap small" style={{minWidth:'150px'}}>{l.details}</td>
                                                <td className="text-center text-dark">{l.price?.toLocaleString()}</td>
                                                <td className="text-center text-dark">{l.quantity}</td>
                                                <td className="text-end pe-3 text-dark">{l.total?.toLocaleString()}</td>
                                            </tr>
                                        ))
                                    )}
                                    {selectedParentNumber && netADeduire > 0 && (
                                        <tr className="table-danger text-danger fw-bold">
                                            <td className="ps-3">AVR</td>
                                            <td className="text-wrap">Avoir sur trop-perçu (Facture {selectedParentNumber})</td>
                                            <td className="text-wrap small fst-italic text-danger text-opacity-75">Reçu: {sommeRecue} F - Prestation: {totalPrestationOriginale} F</td>
                                            <td className="text-center">{netADeduire.toLocaleString()}</td>
                                            <td className="text-center">1</td>
                                            <td className="text-end pe-3">{netADeduire.toLocaleString()}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="d-flex flex-column flex-sm-row justify-content-end gap-2 mt-3 pt-3 border-top">
                            {editingId && (
                                <button type="button" className="btn btn-outline-secondary fw-bold px-3" onClick={() => {setEditingId(null); setCurrentLines([]); setSommeRecue(0);}}>
                                    ANNULER
                                </button>
                            )}
                            <button className={`btn fw-bold px-4 text-white ${netADeduire <= 0 ? 'btn-secondary' : 'btn-danger'}`} type="submit" disabled={netADeduire <= 0}>
                                <FaCheck className="me-2"/> {editingId ? 'METTRE À JOUR' : "VALIDER L'AVOIR"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* HISTORIQUE DES AVOIRS */}
            <div className="card shadow-sm border-0 overflow-hidden">
                <div className="card-header bg-light p-3 d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-3 border-bottom">
                    <span className="fw-bold text-dark text-uppercase" style={{ letterSpacing: '1px' }}>
                        <FaHistory className="me-2 text-danger"/>HISTORIQUE DES AVOIRS
                    </span>
                    <div className="input-group input-group-sm w-100 w-sm-auto" style={{maxWidth: '300px'}}>
                        <span className="input-group-text bg-white text-muted border-end-0"><FaSearch /></span>
                        <input className="form-control border-start-0" placeholder="Rechercher..." onChange={(e) => setSearchHistory(e.target.value)} />
                    </div>
                </div>
                <div className="table-responsive" style={{ maxHeight: '400px' }}>
                    <table className="table table-sm table-hover mb-0 text-nowrap align-middle">
                        <thead className="bg-light sticky-top">
                            <tr className="text-uppercase text-muted" style={{ letterSpacing: '1px', fontSize: '0.85rem' }}>
                                <th className="ps-3 py-3 border-bottom-0">N° Avoir</th>
                                <th className="py-3 border-bottom-0">Client</th>
                                <th className="py-3 border-bottom-0">Réf Facture</th>
                                <th className="py-3 border-bottom-0">Montant</th>
                                <th className="text-end pe-3 py-3 border-bottom-0">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {avoirs.filter(a => a.number.includes(searchHistory) || a.clientName.toLowerCase().includes(searchHistory.toLowerCase())).length === 0 ? (
                                <tr><td colSpan="5" className="text-center py-4 text-muted">Aucun avoir trouvé.</td></tr>
                            ) : (
                                avoirs.filter(a => a.number.includes(searchHistory) || a.clientName.toLowerCase().includes(searchHistory.toLowerCase())).map(a => (
                                    <tr key={a._id}>
                                        <td className="ps-3 fw-bold text-danger">{a.number}</td>
                                        <td className="text-wrap text-dark fw-bold" style={{minWidth: '150px'}}>{a.clientName}</td>
                                        <td className="text-muted small">{a.parentInvoice}</td>
                                        <td className="fw-bold text-danger">-{a.totalTTC?.toLocaleString()} F</td>
                                        <td className="text-end pe-3">
                                            <button className="btn btn-sm btn-outline-primary rounded-1 me-1 py-1 px-2" onClick={() => startEdit(a)} title="Modifier">
                                                <FaEdit size={14}/>
                                            </button>
                                            <button 
                                                className="btn btn-sm btn-outline-danger rounded-1 me-1 py-1 px-2"
                                                onClick={() => handleDownloadPDF(a._id, a.number)}
                                                disabled={downloadingPdfId === a._id}
                                                title="Télécharger PDF"
                                            >
                                                {downloadingPdfId === a._id ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : <FaFilePdf size={14}/>}
                                            </button>
                                            {user?.role === 'admin' && (
                                                <button className="btn btn-sm btn-outline-secondary rounded-1 py-1 px-2" onClick={() => handleDelete(a._id)} title="Supprimer">
                                                    <FaTrash size={14}/>
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default FactureAvoirPage;